import { DialogArPhotoLocked } from '@/components/Pages/spots/components/dialogs/DialogArPhotoLocked'
import { DialogAudioGuideLocked } from '@/components/Pages/spots/components/dialogs/DialogAudioGuideLocked'
import { DialogSpotsAudioGuide } from '@/components/Pages/spots/components/dialogs/DialogSpotsAudioGuide'
import { useApi } from '@/core/api/useApi'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { AppMachineContext } from '@/core/app/useRaundLifeCycle'
import { postWithLocationRetry } from '@/core/geolocation/locationRetry'
import type { GeoLocationForLock } from '@/domain/spot/calcSpotLockStatus'
import { useStampPointLabelFromSheetTypes } from '@/domain/stamp/useStampPointLabel'
import {
  type SpotData,
  isAudioGuideLocked,
  needsLocationForAudioGuides,
  toAudioGuideDialogItems,
} from '@/schemas/v2/spot'
import { useSelector } from '@xstate/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SpotOverview } from './SpotOverview'

type Props = {
  spots: SpotData[]
  onArPhotoClick?: (spot: SpotData) => void
  geoLocation: GeoLocationForLock
  /** シートID→名前（バッジ表示用）。一覧レスポンスの sheets[] から生成する。 */
  sheetNameById?: Map<number, string>
}

export function SpotList({ spots, onArPhotoClick, geoLocation, sheetNameById = new Map() }: Props) {
  const { t } = useTranslation()
  const { gen } = useCampaignSlug()
  const [audioGuideSpot, setAudioGuideSpot] = useState<SpotData | null>(null)
  const [audioGuideItems, setAudioGuideItems] = useState<{ caption: string | null; audio: string | null }[] | null>(
    null,
  )
  const [arPhotoLockedDialogOpen, setArPhotoLockedDialogOpen] = useState(false)
  const [audioGuideLockedDialogOpen, setAudioGuideLockedDialogOpen] = useState(false)
  // ロック時に「端末の位置情報設定をご確認ください」を出すか。このタップで測位を試みて失敗したときだけ true（#3583）
  const [audioGuideLocationUnavailable, setAudioGuideLocationUnavailable] = useState(false)
  // 音声ガイドAPI呼び出し中の二重送信防止（描画不要なため useRef で管理）。
  // 一覧は複数スポットが並ぶため、スポット単位で保持しないと別スポットのタップまで握り潰す
  const audioGuideInFlightRef = useRef<Set<number>>(new Set())

  const appActorRef = AppMachineContext.useActorRef()
  const campaignSheetTypes = useSelector(appActorRef, (s) => s.context.campaignData?.sheetTypes ?? [])
  const { label: stampPointLabel } = useStampPointLabelFromSheetTypes(campaignSheetTypes)

  const api = useApi()

  // 音声ガイドボタン押下時（#3445 / #3583）:
  // 取得済みの座標があれば付けて、なければ座標なしで POST し、
  // サーバーが location_required を返したときだけ測位して再 POST する
  const handleAudioGuideClick = async (spot: SpotData) => {
    if (audioGuideInFlightRef.current.has(spot.id)) return
    audioGuideInFlightRef.current.add(spot.id)
    try {
      const { response: res, locationDenied } = await postWithLocationRetry(
        (body) => api.guest.postSpotAudioGuides(spot.id, body),
        (r) => r !== null && needsLocationForAudioGuides(r.audioGuides),
        geoLocation,
      )
      if (!res) {
        setAudioGuideLocationUnavailable(false)
        setAudioGuideLockedDialogOpen(true)
        return
      }
      const entries = res.audioGuides
      if (isAudioGuideLocked(entries)) {
        setAudioGuideLocationUnavailable(locationDenied)
        setAudioGuideLockedDialogOpen(true)
      } else {
        setAudioGuideItems(toAudioGuideDialogItems(entries))
        setAudioGuideSpot(spot)
      }
    } catch {
      setAudioGuideLocationUnavailable(false)
      setAudioGuideLockedDialogOpen(true)
    } finally {
      audioGuideInFlightRef.current.delete(spot.id)
    }
  }

  if (spots.length === 0) {
    return <p className="p-4 text-center text-gray-400">{t('spot.not_found', 'スポットが見つかりません')}</p>
  }

  return (
    <>
      <ul data-testid="spot-list" className="h-full overflow-y-auto divide-y divide-gray-100">
        {spots.map((spot) => {
          // 音声ガイド・ARフォトのロック判定はサブエンドポイント呼び出し時にバックエンドが行うため常に false
          const arPhotoLocked = false
          const audioGuideLocked = false
          const sheetNames = spot.sheetIds
            .map((id) => sheetNameById.get(id))
            .filter((name): name is string => name !== undefined)
          return (
            <li key={spot.id} data-testid="spot-list-item">
              <SpotOverview
                spot={spot}
                href={gen(`/spots/detail?id=${spot.id}`)}
                sheetNames={sheetNames}
                tags={spot.tags.map((t) => t.name)}
                hasAudioGuide={spot.audioGuide}
                audioGuideActive={spot.audioGuide}
                audioGuideLocked={false}
                onAudioGuideClick={() => handleAudioGuideClick(spot)}
                hasArPhoto={spot.photoFrame}
                arPhotoLocked={false}
                onArPhotoClick={() => {
                  onArPhotoClick?.(spot)
                }}
                geoLoading={!geoLocation.isLoaded || geoLocation.requesting === true}
              />
            </li>
          )
        })}
      </ul>
      {audioGuideSpot && (
        <DialogSpotsAudioGuide
          scrollLock
          onClose={() => {
            setAudioGuideSpot(null)
            setAudioGuideItems(null)
          }}
          audioGuides={audioGuideItems ?? undefined}
        />
      )}
      {arPhotoLockedDialogOpen && (
        <DialogArPhotoLocked
          scrollLock
          stampLabel={stampPointLabel}
          onClose={() => setArPhotoLockedDialogOpen(false)}
        />
      )}
      {audioGuideLockedDialogOpen && (
        <DialogAudioGuideLocked
          scrollLock
          locationUnavailable={audioGuideLocationUnavailable}
          onClose={() => setAudioGuideLockedDialogOpen(false)}
        />
      )}
    </>
  )
}
